import React, { ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import { FlatList, NativeScrollEvent, NativeSyntheticEvent, View } from 'react-native';

import { ArrowButton } from '@/components/ArrowButton';
import { SizedBox } from '@/components/SizedBox';
import { Box } from '@/components/lib/components';
import { CarouselSkeleton } from '@/feature/lobby/components/Skeletons/CarouselSkeleton';
import { common } from '@/styles/styles';
import { isWeb } from '@/utils/constants-platform-specific';

type CarouselItem = {
    title: string;
    description: string;
};

interface PropsCustomRenderer<T> {
    data: T[];
    isLoading: boolean;
    renderCarouselItem: (data: T) => ReactElement;
    renderFooter?: () => ReactElement;
    renderHeader?: () => ReactElement;
    headerWidth?: number;
    showCarouselArrows?: boolean;
}

interface PropsDefault {
    data: CarouselItem[];
    isLoading: boolean;
    showCarouselArrows?: boolean;
}

type Props<T> = PropsCustomRenderer<T> | PropsDefault;

export const carouselItemWidth = 334;
export const carouselItemHeight = 123;
export const itemSeparatorWidth = 12;
const itemOffset = carouselItemWidth + itemSeparatorWidth;

const skeletonMockedData = isWeb ? new Array(5) : new Array(2);

export function Carousel<T>({ data, isLoading, showCarouselArrows = false, ...rest }: Props<T>) {
    const slider = useRef<FlatList>(null);
    const scrollPositionRef = useRef(0);
    const [arrowVisibility, setArrowVisibility] = useState({ showLeft: false, showRight: true });

    const scroll = useCallback((newOffset: number) => {
        slider.current?.scrollToOffset({ offset: newOffset, animated: true });
        scrollPositionRef.current = newOffset;
    }, []);

    const carouselData = isLoading ? skeletonMockedData : data;

    const updateArrowVisibility = useCallback((offset: number, maxScrollOffset: number) => {
        setArrowVisibility({
            showLeft: offset > 0,
            showRight: offset < maxScrollOffset,
        });
    }, []);

    const resolvedHeaderWidth = 'headerWidth' in rest ? rest.headerWidth ?? 0 : 0;

    const offsetsArray = useMemo(
        () => [...Array(carouselData.length).keys()].map(i => resolvedHeaderWidth + i * itemOffset - 16),
        [carouselData.length, resolvedHeaderWidth]
    );

    const renderItem = useCallback(
        ({ item, index }: { item: T; index: number }) => {
            if (isLoading) {
                return <CarouselSkeleton />;
            }

            return (
                <Box key={index} height={carouselItemHeight} width={carouselItemWidth}>
                    {'renderCarouselItem' in rest ? rest.renderCarouselItem(item) : null}
                </Box>
            );
        },
        [isLoading, rest]
    );

    const onScroll = useCallback(
        (e: NativeSyntheticEvent<NativeScrollEvent>) => {
            const offset = e.nativeEvent.contentOffset.x;
            const layoutWidth = e.nativeEvent.layoutMeasurement.width;
            const contentWidth = e.nativeEvent.contentSize.width;
            const maxScrollOffset = contentWidth - layoutWidth;

            scrollPositionRef.current = offset;
            if (showCarouselArrows) {
                updateArrowVisibility(offset, maxScrollOffset);
            }
        },
        [showCarouselArrows, updateArrowVisibility]
    );

    if (!isLoading && carouselData.length === 0) {
        return null;
    }

    return (
        <View testID="carouselFlatList" style={common.justifyCenter}>
            {showCarouselArrows && arrowVisibility.showLeft ? (
                <ArrowButton
                    direction="left"
                    onPress={() => scroll(Math.max(scrollPositionRef.current - itemOffset, 0))}
                />
            ) : null}
            <FlatList
                ref={slider}
                horizontal
                showsHorizontalScrollIndicator={false}
                snapToOffsets={offsetsArray}
                decelerationRate={'fast'}
                overScrollMode={'never'}
                scrollEventThrottle={500}
                windowSize={isWeb ? undefined : 5}
                ItemSeparatorComponent={SpacingSeparator}
                contentContainerStyle={common.paddingHorizontal}
                data={carouselData}
                style={common.noGrow}
                testID="carouselFlatList"
                // on web, the FlatList doesn't load the next page when scrolling the the carousel
                // on mobile, only the first 2 items are visible
                initialNumToRender={isWeb ? carouselData.length : 2}
                onScroll={onScroll}
                renderItem={renderItem}
                ListFooterComponent={'renderFooter' in rest ? rest.renderFooter : undefined}
                ListHeaderComponent={'renderHeader' in rest ? rest.renderHeader : undefined}
            />
            {showCarouselArrows && arrowVisibility.showRight ? (
                <ArrowButton direction="right" onPress={() => scroll(scrollPositionRef.current + itemOffset)} />
            ) : null}
        </View>
    );
}

const SpacingSeparator = () => <SizedBox value={itemSeparatorWidth} />;
